/**
 * packages/dados/src/documentos.ts — os arquivos anexados, com a hierarquia
 * incorporadora › empreendimento.
 *
 * ONDE ESTE ARQUIVO SE ENCAIXA
 *   cliente.ts       → conexão (`obterCliente`, `ErroFincast`)
 *   repositorios.ts  → as consultas e gravações do domínio
 *   **documentos.ts**→ arquivo: enviar, listar, baixar, remover, desfazer
 *
 * POR QUE É UM ARQUIVO À PARTE, E NÃO MAIS UMA SEÇÃO DE `repositorios.ts`
 * Aqui há DOIS servidores, não um: o banco (PostgREST, com RLS) e o serviço de
 * armazenamento (outra API, com políticas próprias em `storage.objects`). Toda
 * operação atravessa os dois, e a ordem entre eles é regra — misturar isso com o
 * resto do domínio esconderia a costura.
 *
 * ⚠ A REGRA DE ORDEM, E POR QUE ELA É ESTA
 * Enviar são duas chamadas: sobe o objeto, depois grava a linha. Nesta ordem, e não
 * na inversa, porque:
 *   · o objeto sozinho é INALCANÇÁVEL — a política de leitura de `storage.objects`
 *     pergunta a `app.documento`, e sem linha ninguém baixa nada. Um objeto órfão é
 *     lixo invisível, não vazamento.
 *   · a linha sozinha seria um documento que aparece na lista e não baixa: erro na
 *     cara do usuário, num lugar em que ele não pode fazer nada.
 * Se a segunda chamada falhar, a primeira é DESFEITA (o objeto é apagado) e o erro
 * sobe. É o mesmo raciocínio do "publicar são 2 chamadas" de `repositorios.ts`, com
 * a diferença de que aqui dá para limpar.
 *
 * ⚠ NINGUÉM APAGA NADA DE VERDADE. Remover é `removido_em` preenchido — a linha
 * continua visível para a incorporadora (é o que faz o desfazer funcionar) e o objeto
 * continua no compartimento. A única exclusão real deste arquivo é a limpeza do
 * objeto órfão logo acima, que acontece antes de existir documento algum.
 */

import { ErroFincast, obterCliente } from './cliente';
import { conexao, ehFalhaDeConexao } from './conexao';
import { obterPerfil } from './sessao';
import type { Database } from './banco.tipos';

/** O compartimento privado criado pela migration 0024. */
export const COMPARTIMENTO = 'documentos';

/** Teto do arquivo: 20 MB. O mesmo número está no compartimento e no check da tabela. */
export const TAMANHO_MAXIMO_BYTES = 20 * 1024 * 1024;

/**
 * Os tipos aceitos, com a extensão que os acompanha.
 *
 * ⚠ Esta lista é CONVENIÊNCIA, não segurança: quem recusa de verdade é o serviço de
 * armazenamento (`allowed_mime_types` do compartimento), que o cliente não contorna.
 * Aqui ela existe para o usuário receber a recusa ANTES de subir 20 MB.
 */
export const TIPOS_ACEITOS: ReadonlyArray<{ mime: string; extensoes: string[]; rotulo: string }> = [
  { mime: 'application/pdf', extensoes: ['.pdf'], rotulo: 'PDF' },
  {
    mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    extensoes: ['.xlsx'], rotulo: 'Excel',
  },
  { mime: 'application/vnd.ms-excel', extensoes: ['.xls'], rotulo: 'Excel' },
  { mime: 'image/png', extensoes: ['.png'], rotulo: 'Imagem' },
  { mime: 'image/jpeg', extensoes: ['.jpg', '.jpeg'], rotulo: 'Imagem' },
  { mime: 'image/webp', extensoes: ['.webp'], rotulo: 'Imagem' },
];

export type TipoDocumento = Database['app']['Enums']['tipo_documento'];

/** Um documento como a tela precisa dele. */
export interface Documento {
  id: string;
  empresaId: string;
  empreendimentoId: string;
  cenarioId: string | null;
  tipo: TipoDocumento;
  nome: string;
  tamanhoBytes: number;
  mime: string;
  caminho: string;
  sha256: string;
  enviadoPor: string;
  enviadoEm: string;
  removidoEm: string | null;
}

export interface NovoDocumento {
  empresaId: string;
  empreendimentoId: string;
  /** De qual cenário o usuário anexou. Informativo — o documento é do empreendimento. */
  cenarioId?: string | null;
  tipo: TipoDocumento;
  arquivo: File;
}

/* =============================================================================
 * Erros
 * ========================================================================== */

function erro(codigo: string, mensagem: string, causa?: unknown): ErroFincast {
  return new ErroFincast(codigo, mensagem, causa);
}

/**
 * O serviço de armazenamento não fala PostgREST: os erros dele vêm com outra forma
 * (`statusCode` em texto, `error`, `message`) e precisam virar frase em português.
 * A queda de rede vem primeiro — sem resposta não há código nenhum.
 */
function traduzir(e: unknown, operacao: string): ErroFincast {
  if (e instanceof ErroFincast) return e;
  if (ehFalhaDeConexao(e)) {
    return erro('SEM_CONEXAO', 'Sem conexão com o servidor. Tente de novo em instantes.', { operacao, erro: e });
  }
  const o = (e ?? {}) as Record<string, unknown>;
  const texto = [o['message'], o['error'], o['details']].filter((x) => typeof x === 'string').join(' | ');
  const status = String(o['statusCode'] ?? o['status'] ?? '');

  if (status === '413' || /exceeded the maximum allowed size|payload too large/i.test(texto)) {
    return erro('ARQUIVO_GRANDE', `O arquivo passa do limite de ${Math.round(TAMANHO_MAXIMO_BYTES / 1024 / 1024)} MB.`, { operacao, erro: e });
  }
  if (/mime type .* is not supported|invalid_mime_type/i.test(texto)) {
    return erro('TIPO_NAO_ACEITO', 'Esse tipo de arquivo não é aceito. Envie PDF, Excel ou imagem.', { operacao, erro: e });
  }
  if (status === '409' || /already exists|duplicate/i.test(texto)) {
    return erro('JA_EXISTE', 'Já existe um arquivo com esse endereço. Tente enviar de novo.', { operacao, erro: e });
  }
  /* 400 do armazenamento com "row-level security" é a política do compartimento
     negando — e é o que acontece quando o usuário não pode escrever no empreendimento. */
  if (status === '403' || status === '401' || /row-level security|not authorized|permission denied/i.test(texto)) {
    return erro('SEM_PERMISSAO', 'Você não tem permissão para isso neste empreendimento.', { operacao, erro: e });
  }
  if (status === '404' || /not found|object not found/i.test(texto)) {
    return erro('NAO_ENCONTRADO', 'Arquivo não encontrado — ou fora do seu acesso.', { operacao, erro: e });
  }
  return erro('FALHA_ARMAZENAMENTO', 'Não foi possível concluir a operação com o arquivo.', { operacao, erro: e, detalhe: texto });
}

/* =============================================================================
 * Apoio
 * ========================================================================== */

function app() {
  return obterCliente();
}

async function idDoUsuario(operacao: string): Promise<string> {
  const perfil = await obterPerfil();
  if (perfil === null) {
    throw erro('NAO_AUTENTICADO', 'Você precisa entrar para usar o Fincast.', { operacao });
  }
  return perfil.id;
}

/**
 * SHA-256 do conteúdo, em hexadecimal.
 *
 * É o que a validação usa para provar que o arquivo volta IDÊNTICO — tamanho igual
 * não prova nada. Usa a WebCrypto, que existe no navegador e no Node ≥ 18.
 */
export async function hashDoArquivo(dados: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', dados);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Nome seguro para o objeto: só o que sobrevive a um caminho de URL, com um uuid na
 * frente. O uuid é o que impede dois envios do mesmo arquivo de colidirem — e o
 * caminho é `unique` na tabela.
 */
function nomeNoCompartimento(nomeOriginal: string): string {
  const limpo = nomeOriginal
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')   /* tira acento, mantém a letra */
    .replace(/[^A-Za-z0-9._-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^[-.]+|[-.]+$/g, '')
    .slice(-120);
  return `${crypto.randomUUID()}-${limpo === '' ? 'arquivo' : limpo}`;
}

/** O tipo aceito que casa com este arquivo, ou `null`. Casa por mime OU por extensão. */
export function tipoAceitoDe(arquivo: { name: string; type: string }): string | null {
  const nome = arquivo.name.toLowerCase();
  for (const t of TIPOS_ACEITOS) {
    if (arquivo.type === t.mime) return t.mime;
    if (t.extensoes.some((ext) => nome.endsWith(ext))) return t.mime;
  }
  return null;
}

/**
 * Recusa ANTES de subir. Devolve a frase de tela, ou `null` quando está tudo bem.
 * ⚠ Não é a barreira de segurança — é a cortesia. A barreira está no compartimento.
 */
export function motivoDeRecusa(arquivo: { name: string; type: string; size: number }): string | null {
  if (arquivo.size <= 0) return 'O arquivo está vazio.';
  if (arquivo.size > TAMANHO_MAXIMO_BYTES) {
    /* ⚠ Arredonda para CIMA, não para o mais próximo. Com `toFixed`, um arquivo de
       20 MB + 1 byte virava "O arquivo tem 20,0 MB e o limite é 20 MB" — uma recusa que
       parece defeito. Para cima, o número mostrado é sempre MAIOR que o limite, que é a
       verdade do caso. */
    const mb = (Math.ceil((arquivo.size / 1024 / 1024) * 10) / 10).toFixed(1).replace('.', ',');
    return `O arquivo tem ${mb} MB e o limite é ${Math.round(TAMANHO_MAXIMO_BYTES / 1024 / 1024)} MB.`;
  }
  if (tipoAceitoDe(arquivo) === null) return 'Esse tipo de arquivo não é aceito. Envie PDF, Excel ou imagem.';
  return null;
}

type LinhaDocumento = Database['app']['Tables']['documento']['Row'];

function paraDocumento(l: LinhaDocumento): Documento {
  return {
    id: l.id,
    empresaId: l.empresa_id,
    empreendimentoId: l.empreendimento_id,
    cenarioId: l.cenario_id,
    tipo: l.tipo,
    nome: l.nome_original,
    tamanhoBytes: Number(l.tamanho_bytes),
    mime: l.mime,
    caminho: l.caminho,
    sha256: l.sha256,
    enviadoPor: l.enviado_por,
    enviadoEm: l.enviado_em,
    removidoEm: l.removido_em,
  };
}

/* =============================================================================
 * Operações
 * ========================================================================== */

/**
 * Envia o arquivo e grava o registro. Ver a REGRA DE ORDEM no topo do arquivo.
 *
 * O caminho é montado AQUI e conferido de novo pelo banco: o check
 * `documento_caminho_hierarquia` recusa qualquer caminho que não descreva as colunas
 * da própria linha. Não é o cliente que decide onde o arquivo mora.
 */
export async function enviarDocumento(novo: NovoDocumento): Promise<Documento> {
  const recusa = motivoDeRecusa(novo.arquivo);
  if (recusa !== null) throw erro('ARQUIVO_RECUSADO', recusa, { operacao: 'enviar documento' });

  const uid = await idDoUsuario('enviar documento');
  const conteudo = await novo.arquivo.arrayBuffer();
  const sha256 = await hashDoArquivo(conteudo);
  const mime = tipoAceitoDe(novo.arquivo) ?? novo.arquivo.type;
  const caminho = `${novo.empresaId}/${novo.empreendimentoId}/${nomeNoCompartimento(novo.arquivo.name)}`;

  /* 1) o objeto. Enquanto não houver linha, ele é inalcançável por qualquer sessão. */
  const envio = await obterCliente().storage.from(COMPARTIMENTO).upload(caminho, conteudo, {
    contentType: mime,
    upsert: false,   /* o uuid do nome já garante unicidade; upsert esconderia colisão */
  });
  if (envio.error) {
    conexao.registrarFalha(envio.error);
    throw traduzir(envio.error, 'enviar documento');
  }
  conexao.registrarSucesso();

  /* 2) a linha. Falhou? o objeto vira lixo invisível — então limpamos. */
  const gravacao = await app()
    .from('documento')
    .insert({
      empresa_id: novo.empresaId,
      empreendimento_id: novo.empreendimentoId,
      cenario_id: novo.cenarioId ?? null,
      tipo: novo.tipo,
      nome_original: novo.arquivo.name.slice(0, 255),
      tamanho_bytes: novo.arquivo.size,
      mime,
      caminho,
      sha256,
      enviado_por: uid,
    })
    .select()
    .single();

  if (gravacao.error) {
    conexao.registrarFalha(gravacao.error);
    /* Melhor esforço: se a limpeza também falhar, o objeto fica órfão e inalcançável.
       Não escondemos o erro original por causa dela. */
    try {
      await obterCliente().storage.from(COMPARTIMENTO).remove([caminho]);
    } catch { /* o erro que importa é o de baixo */ }
    throw traduzir(gravacao.error, 'gravar documento');
  }
  conexao.registrarSucesso();
  return paraDocumento(gravacao.data);
}

/**
 * Os documentos de um empreendimento, do mais novo para o mais antigo.
 * Os removidos ficam de fora — a linha existe, mas a lista é do que está valendo.
 * Empreendimento arquivado devolve lista vazia: quem esconde é a RLS, não este código.
 */
export async function listarDocumentos(empreendimentoId: string): Promise<Documento[]> {
  const r = await app()
    .from('documento')
    .select('*')
    .eq('empreendimento_id', empreendimentoId)
    .is('removido_em', null)
    .order('enviado_em', { ascending: false });
  if (r.error) {
    conexao.registrarFalha(r.error);
    throw traduzir(r.error, 'listar documentos');
  }
  conexao.registrarSucesso();
  return (r.data ?? []).map(paraDocumento);
}

/**
 * Todos os documentos válidos da incorporadora, do mais novo para o mais antigo.
 *
 * UMA ida ao servidor para a área inteira, e não uma por pasta: a tela de Documentos
 * mostra todos os empreendimentos ao mesmo tempo, e N consultas fariam a área abrir
 * devagar e piorar com o crescimento da incorporadora. O agrupamento por empreendimento
 * é da tela; aqui sai a lista crua, já ordenada.
 *
 * Empreendimento arquivado não aparece — quem esconde é a RLS (`doc_sel` exige o
 * empreendimento visível), não este código.
 */
export async function listarDocumentosDaEmpresa(empresaId: string): Promise<Documento[]> {
  const r = await app()
    .from('documento')
    .select('*')
    .eq('empresa_id', empresaId)
    .is('removido_em', null)
    .order('enviado_em', { ascending: false });
  if (r.error) {
    conexao.registrarFalha(r.error);
    throw traduzir(r.error, 'listar documentos da incorporadora');
  }
  conexao.registrarSucesso();
  return (r.data ?? []).map(paraDocumento);
}

/**
 * Endereço temporário para baixar. O compartimento é privado: sem assinatura não há
 * download, e a assinatura só é emitida se a política de leitura deixar — e ela
 * pergunta a `app.documento`, sob a RLS de `app.documento`.
 *
 * @param segundos validade do endereço. Curta de propósito: o endereço assinado
 *                 funciona sem sessão, então ele é a credencial enquanto vive.
 */
export async function enderecoParaBaixar(caminho: string, segundos = 60): Promise<string> {
  const r = await obterCliente().storage.from(COMPARTIMENTO).createSignedUrl(caminho, segundos);
  if (r.error || !r.data?.signedUrl) {
    conexao.registrarFalha(r.error);
    throw traduzir(r.error ?? { message: 'sem endereço assinado' }, 'baixar documento');
  }
  conexao.registrarSucesso();
  return r.data.signedUrl;
}

/**
 * Remove da lista. Não apaga: preenche `removido_em`/`removido_por`, o objeto fica
 * onde está e `restaurarDocumento` desfaz.
 *
 * A linha removida CONTINUA visível pela RLS de propósito — é o que permite achá-la
 * de volta. Esconder pela política recriaria o beco sem saída da migration 0021.
 */
export async function removerDocumento(id: string): Promise<Documento> {
  const uid = await idDoUsuario('remover documento');
  const r = await app()
    .from('documento')
    .update({ removido_em: new Date().toISOString(), removido_por: uid })
    .eq('id', id)
    .select()
    .single();
  if (r.error) {
    conexao.registrarFalha(r.error);
    throw traduzir(r.error, 'remover documento');
  }
  conexao.registrarSucesso();
  return paraDocumento(r.data);
}

/** Desfaz a remoção. O par `removido_em`/`removido_por` volta a nulo — o check exige os dois. */
export async function restaurarDocumento(id: string): Promise<Documento> {
  const r = await app()
    .from('documento')
    .update({ removido_em: null, removido_por: null })
    .eq('id', id)
    .select()
    .single();
  if (r.error) {
    conexao.registrarFalha(r.error);
    throw traduzir(r.error, 'restaurar documento');
  }
  conexao.registrarSucesso();
  return paraDocumento(r.data);
}
